import React, { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
  PieChart, Pie, Cell,
} from 'recharts';
import { MetricCard, Card, Plate } from '../components/Card';
import { getSeoCounters, getSeoSite } from '../api';

const PIE_COLORS = ['#00a7dc', '#0e9f6e', '#e08a00', '#00a7dc', '#e04848', 'var(--text2)'];
const tooltipStyle = { background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 };

const RANGES = [
  { key: '7', label: 'Неделя', days: 7 },
  { key: '14', label: '2 недели', days: 14 },
  { key: '30', label: 'Месяц', days: 30 },
  { key: '90', label: '3 месяца', days: 90 },
  { key: '180', label: 'Полгода', days: 180 },
  { key: '365', label: 'Год', days: 365 },
];

function iso(d) {
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const MONTH_SHORT = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

// Текущий месяц и 11 предыдущих; ключ периода — «m:ГГГГ-ММ».
function lastMonths(count = 12) {
  const now = new Date();
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    return { key: `m:${ym}`, label: `${MONTH_SHORT[d.getMonth()]} ${String(d.getFullYear()).slice(2)}` };
  });
}

function monthToDates(ym) {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return null;
  const today = new Date();
  const from = new Date(y, m - 1, 1);
  if (from > today) return null;
  const last = new Date(y, m, 0);
  return { date1: iso(from), date2: iso(last > today ? today : last) };
}

function fmt(n) {
  return Number(n || 0).toLocaleString('ru-RU');
}

function pct(n) {
  return String(n ?? 0).replace('.', ',');
}

function num(n, digits = 0) {
  return Number(n || 0).toLocaleString('ru-RU', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function ruDate(s) {
  return (s || '').split('-').reverse().join('.');
}

function signOf(v) {
  return v > 0 ? '+' : v < 0 ? '−' : '';
}

// Тренд к прошлому месяцу: рост — зелёный, падение — красный.
// Нет данных за прошлый месяц — блока нет (нули не рисуем).
function Trend({ t, prev, unit = '', digits = 0, format }) {
  if (!t || !prev) return null;
  const color = t.diff > 0 ? '#0e9f6e' : t.diff < 0 ? '#e04848' : 'var(--text2)';
  const arrow = t.diff > 0 ? '▲' : t.diff < 0 ? '▼' : '●';
  const abs = t.unit === 'pp'
    ? `${signOf(t.diff)}${num(Math.abs(t.diff), digits)} п.п.`
    : `${signOf(t.diff)}${num(Math.abs(t.diff))}${unit}`;
  const rel = t.pct == null ? 'с нуля' : `${signOf(t.pct)}${num(Math.abs(t.pct), 1)}%`;
  const label = prev.label === 'к предыдущему месяцу' ? 'к пред. месяцу' : prev.label;
  const show = format || (v => num(v, digits) + unit);
  return (
    <div
      title={`${ruDate(prev.period.from)}–${ruDate(prev.period.to)}: ${show(t.prev)} → сейчас ${show(t.cur)}`}
      style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--border)', whiteSpace: 'nowrap', lineHeight: 1.35 }}
    >
      <div style={{ fontSize: 13, fontWeight: 800, color }}>{arrow} {abs}</div>
      <div style={{ fontSize: 12, marginTop: 2 }}>
        <span style={{ color, fontWeight: 700 }}>{rel}</span>
        <span style={{ color: 'var(--text2)' }}> {label}</span>
      </div>
    </div>
  );
}

function rangeToDates(rangeKey, custom) {
  if (rangeKey === 'custom') {
    return (custom.from && custom.to) ? { date1: custom.from, date2: custom.to } : null;
  }
  if (rangeKey.startsWith('m:')) {
    const md = monthToDates(rangeKey.slice(2));
    if (md) return md;
  }
  const days = (RANGES.find(r => r.key === rangeKey) || RANGES[2]).days;
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - (days - 1));
  return { date1: iso(from), date2: iso(to) };
}

function CityTrend({ t }) {
  if (!t) return null;
  const color = t.diff > 0 ? '#0e9f6e' : t.diff < 0 ? '#e04848' : 'var(--text2)';
  const arrow = t.diff > 0 ? '▲' : t.diff < 0 ? '▼' : '●';
  const rel = t.pct == null ? 'с нуля' : `${signOf(t.pct)}${num(Math.abs(t.pct), 1)}%`;
  return (
    <div style={{ marginTop: 2, fontSize: 11, fontWeight: 700, color, whiteSpace: 'nowrap' }}>
      {arrow} {signOf(t.diff)}{fmt(Math.abs(t.diff))} · {rel}
    </div>
  );
}

function CityMetric({ value, trend }) {
  return (
    <div>
      <div style={{ fontWeight: 800 }}>{fmt(value)}</div>
      <CityTrend t={trend} />
    </div>
  );
}

function CityTable({ cities, prev }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            {['#', 'Город', 'Визиты', 'Из поиска', 'Заявки с формы'].map((h, i) => (
              <th key={h} style={{
                textAlign: i < 2 ? 'left' : 'right',
                padding: '8px 10px',
                color: 'var(--text2)',
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                borderBottom: '1px solid var(--border)',
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {cities.map(row => (
            <tr key={row.host}>
              <td style={{ padding: '10px', borderBottom: '1px solid var(--border)', color: 'var(--text2)', width: 36 }}>{row.rank}</td>
              <td style={{ padding: '10px', borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontWeight: 700 }}>{row.city}</div>
                <div style={{ color: 'var(--text2)', fontSize: 11, marginTop: 2 }}>{row.host}</div>
              </td>
              <td style={{ padding: '10px', borderBottom: '1px solid var(--border)', textAlign: 'right' }}>
                <CityMetric value={row.visits} trend={row.trends?.visits} />
              </td>
              <td style={{ padding: '10px', borderBottom: '1px solid var(--border)', textAlign: 'right' }}>
                <CityMetric value={row.organic} trend={row.trends?.organic} />
              </td>
              <td style={{ padding: '10px', borderBottom: '1px solid var(--border)', textAlign: 'right' }}>
                <CityMetric value={row.forms} trend={row.trends?.forms} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {prev && (
        <div style={{ color: 'var(--text2)', fontSize: 11, marginTop: 8 }}>
          Сортировка по визитам за выбранный период. Тренд {prev.label}: {ruDate(prev.period.from)} – {ruDate(prev.period.to)}. Источник: Яндекс.Метрика, счётчик сети.
        </div>
      )}
    </div>
  );
}

function TrendChart({ monthly }) {
  return (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={monthly} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
        <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="month" tick={{ fill: 'var(--text2)', fontSize: 11 }} minTickGap={12} />
        <YAxis tick={{ fill: 'var(--text2)', fontSize: 11 }} tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}к` : v} />
        <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: 'var(--text1)' }} formatter={(v, n) => [v + ' виз.', n]} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Line type="monotone" dataKey="total" name="Всего визитов" stroke="var(--text2)" strokeWidth={2} dot={false} />
        <Line type="monotone" dataKey="organic" name="Из поиска (organic)" stroke="#0e9f6e" strokeWidth={2.5} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

function EnginesPie({ engines }) {
  const data = (engines || []).slice(0, 6).filter(e => e.visits > 0);
  if (!data.length) return <div style={{ color: 'var(--text2)', padding: '40px 0', textAlign: 'center', fontSize: 13 }}>Нет данных за период</div>;
  return (
    <ResponsiveContainer width="100%" height={260}>
      <PieChart>
        <Pie data={data} dataKey="visits" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={48}
          label={({ name, percent }) => `${name.split(',')[0]} ${(percent * 100).toFixed(0)}%`}
          labelLine={false} style={{ fontSize: 11 }}>
          {data.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
        </Pie>
        <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [v + ' виз.', n]} />
      </PieChart>
    </ResponsiveContainer>
  );
}

function SimpleTable({ headers, rows, aligns = [], empty = 'Нет данных за период' }) {
  if (!rows.length) return <div style={{ color: 'var(--text2)', padding: '24px 0', textAlign: 'center', fontSize: 13 }}>{empty}</div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i} style={{
                textAlign: aligns[i] || 'left', padding: '8px 10px', color: 'var(--text2)',
                fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
                borderBottom: '1px solid var(--border)',
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri}>
              {r.map((c, ci) => (
                <td key={ci} style={{
                  textAlign: aligns[ci] || 'left', padding: '8px 10px',
                  borderBottom: '1px solid var(--border)', color: 'var(--text1)',
                }}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PriorityPill({ value }) {
  const color = value === 'Высокий' ? '#0e9f6e' : value === 'Средний' ? '#e08a00' : 'var(--text2)';
  return <span style={{ color, fontWeight: 700, fontSize: 12 }}>{value}</span>;
}

function SemCore({ items, title }) {
  if (!items || !items.length) return null;
  return (
    <Card title={title} style={{ marginBottom: 16 }}>
      <SimpleTable
        headers={['Кластер', 'Примеры запросов', 'Посадочная', 'Приоритет']}
        aligns={['left', 'left', 'left', 'center']}
        rows={items.map(c => [
          <b>{c.cluster}</b>,
          <span style={{ color: 'var(--text2)' }}>{c.examples}</span>,
          <code style={{ fontSize: 12 }}>{c.landing}</code>,
          <PriorityPill value={c.priority} />,
        ])}
      />
    </Card>
  );
}

const pillBtn = (active) => ({
  padding: '7px 12px', borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
  border: '1px solid ' + (active ? 'var(--accent)' : 'var(--border)'),
  background: active ? 'var(--accent)' : 'var(--bg2)',
  color: active ? '#fff' : 'var(--text2)',
  whiteSpace: 'nowrap',
});

const monthBtn = (active) => ({ ...pillBtn(active), width: 68, textAlign: 'center', padding: '7px 0' });

const periodLabel = {
  color: 'var(--text2)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.05em', marginRight: 4, width: 70,
};

const arrowBtn = {
  width: 34, height: 34, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg2)',
  color: 'var(--text1)', cursor: 'pointer', fontSize: 18, fontWeight: 700, lineHeight: 1,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

export default function Seo() {
  const [counters, setCounters] = useState([]);
  const [counterId, setCounterId] = useState(() => localStorage.getItem('seo_counter') || '');
  const [rangeKey, setRangeKey] = useState(() => localStorage.getItem('seo_range') || '30');
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const months = useMemo(() => lastMonths(12), []);
  const dates = useMemo(() => rangeToDates(rangeKey, custom), [rangeKey, custom]);
  const curIdx = counters.findIndex(c => c.id === counterId);
  const current = counters[curIdx] || null;

  // загрузка списка счётчиков
  useEffect(() => {
    getSeoCounters()
      .then(d => {
        const list = d.counters || [];
        setCounters(list);
        const saved = localStorage.getItem('seo_counter');
        const exists = list.find(c => c.id === saved);
        setCounterId(exists ? saved : (list[0]?.id || ''));
      })
      .catch(() => setError('Не удалось получить список сайтов из Метрики'));
  }, []);

  // загрузка данных при смене сайта / диапазона
  useEffect(() => {
    if (!counterId || !dates) return;
    localStorage.setItem('seo_counter', counterId);
    localStorage.setItem('seo_range', rangeKey);
    let alive = true;
    setLoading(true);
    setError('');
    getSeoSite(counterId, dates.date1, dates.date2)
      .then(d => { if (alive) { setData(d); setLoading(false); } })
      .catch(() => { if (alive) { setError('Не удалось загрузить статистику'); setLoading(false); } });
    return () => { alive = false; };
  }, [counterId, dates, rangeKey]);

  const refresh = () => {
    if (!counterId || !dates) return;
    setLoading(true);
    getSeoSite(counterId, dates.date1, dates.date2, true)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError('Не удалось обновить'); setLoading(false); });
  };

  const step = (delta) => {
    if (!counters.length) return;
    const ni = (curIdx + delta + counters.length) % counters.length;
    setCounterId(counters[ni].id);
  };

  const totals = data?.totals || {};
  const trends = data?.trends || {};
  const prev = data?.prev || null;
  const kdx4 = data?.page_checks?.find(p => p.label.includes('KD-X4'));

  return (
    <div>
      <div className="kp-hero">
        <Plate>Аналитика · SEO</Plate>
        <h1>SEO-аналитика</h1>
        <div style={{ color: 'var(--text2)', fontSize: 13, marginTop: 8 }}>
          Все счётчики Яндекс.Метрики · выбор сайта и периода
        </div>
      </div>

      {/* Панель управления */}
      <Card style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Переключатель сайтов */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button style={arrowBtn} onClick={() => step(-1)} disabled={!counters.length} title="Предыдущий сайт">‹</button>
            <select
              value={counterId}
              onChange={e => setCounterId(e.target.value)}
              style={{
                minWidth: 280, maxWidth: 420, padding: '9px 12px', borderRadius: 8, fontSize: 13.5, fontWeight: 600,
                background: 'var(--bg2)', color: 'var(--text1)', border: '1px solid var(--border)', cursor: 'pointer',
              }}
            >
              {counters.length === 0 && <option>Загрузка…</option>}
              {counters.map(c => (
                <option key={c.id} value={c.id}>
                  {c.known ? '★ ' : ''}{c.name}{c.site ? ` · ${c.site}` : ''}
                </option>
              ))}
            </select>
            <button style={arrowBtn} onClick={() => step(1)} disabled={!counters.length} title="Следующий сайт">›</button>
            {counters.length > 0 && (
              <span style={{ color: 'var(--text2)', fontSize: 12, marginLeft: 4 }}>
                {curIdx + 1} / {counters.length}
              </span>
            )}
          </div>

          {/* Обновить */}
          <button onClick={refresh} disabled={loading || !counterId}
            style={{ padding: '9px 14px', background: loading ? 'var(--bg3)' : 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, cursor: loading ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700 }}>
            {loading ? '⏳ Загрузка…' : '🔄 Обновить'}
          </button>
        </div>

        {/* Календарные месяцы */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
          <span style={periodLabel}>Месяц:</span>
          {months.map(m => (
            <button key={m.key} style={monthBtn(rangeKey === m.key)} onClick={() => setRangeKey(m.key)}>{m.label}</button>
          ))}
        </div>

        {/* Диапазон дат */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 10 }}>
          <span style={periodLabel}>Период:</span>
          {RANGES.map(r => (
            <button key={r.key} style={pillBtn(rangeKey === r.key)} onClick={() => setRangeKey(r.key)}>{r.label}</button>
          ))}
          <button style={pillBtn(rangeKey === 'custom')} onClick={() => setRangeKey('custom')}>Свой период</button>
          {rangeKey === 'custom' && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 4 }}>
              <input type="date" value={custom.from} max={custom.to || undefined}
                onChange={e => setCustom(c => ({ ...c, from: e.target.value }))}
                style={{ padding: '6px 8px', borderRadius: 8, background: 'var(--bg2)', color: 'var(--text1)', border: '1px solid var(--border)', fontSize: 12.5 }} />
              <span style={{ color: 'var(--text2)' }}>—</span>
              <input type="date" value={custom.to} min={custom.from || undefined} max={iso(new Date())}
                onChange={e => setCustom(c => ({ ...c, to: e.target.value }))}
                style={{ padding: '6px 8px', borderRadius: 8, background: 'var(--bg2)', color: 'var(--text1)', border: '1px solid var(--border)', fontSize: 12.5 }} />
            </span>
          )}
          {dates && (
            <span style={{ color: 'var(--text2)', fontSize: 12, marginLeft: 'auto' }}>
              {dates.date1} … {dates.date2}
            </span>
          )}
        </div>
      </Card>

      {error && (
        <Card style={{ marginBottom: 16, borderColor: '#e04848' }}>
          <div style={{ color: '#e04848' }}>{error}</div>
        </Card>
      )}

      {rangeKey === 'custom' && !dates && (
        <Card><div style={{ color: 'var(--text2)', padding: '16px 0', textAlign: 'center' }}>Выберите даты «от» и «до».</div></Card>
      )}

      {loading && !data && (
        <Card><div style={{ color: 'var(--text2)', padding: '20px 0', textAlign: 'center' }}>Загружаем данные из Яндекс.Метрики…</div></Card>
      )}

      {data && (
        <div style={{ opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
          {/* Заголовок текущего сайта */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: current?.known ? '#00a7dc' : 'var(--text2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🔍</div>
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 800, lineHeight: 1 }}>{current?.name || data.counter}</h2>
              <div style={{ color: 'var(--text2)', fontSize: 12, marginTop: 3 }}>
                {data.subtitle || current?.site || `счётчик ${data.counter}`}
              </div>
            </div>
            {prev && (
              <div style={{ marginLeft: 'auto', color: 'var(--text2)', fontSize: 12, textAlign: 'right', whiteSpace: 'nowrap' }}>
                Тренды {prev.label}: {ruDate(prev.period.from)} – {ruDate(prev.period.to)}
              </div>
            )}
          </div>

          {/* KPI */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 14 }}>
            <MetricCard title="Из поиска (organic)" value={fmt(totals.organic)} sub="визитов за период" color="var(--green)"
              footer={<Trend t={trends.organic} prev={prev} />} />
            <MetricCard title="Из соцсетей" value={totals.social == null ? '—' : fmt(totals.social)} sub="ВК, Telegram, YouTube и др." color="#00a7dc"
              footer={<Trend t={trends.social} prev={prev} />} />
            <MetricCard title="Всего визитов" value={fmt(totals.total)} sub="за выбранный период"
              footer={<Trend t={trends.total} prev={prev} />} />
            <MetricCard title="Доля поиска" value={`${totals.share || 0}%`} sub="organic / всего" color="#00a7dc"
              footer={<Trend t={trends.share} prev={prev} unit="%" digits={1} />} />
          </div>

          {data.cities?.length > 0 && (
            <Card title="Города сети — кто в топе по визитам" style={{ marginBottom: 16 }}>
              <CityTable cities={data.cities} prev={prev} />
            </Card>
          )}

          {data.commerce && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 18 }}>
              <MetricCard title="Заказы из поиска" value={fmt(data.commerce.orders)} sub={`всего на сайте ${fmt(data.commerce.orders_all)} · оформление, не оплата`} color="var(--green)"
                footer={<Trend t={trends.orders} prev={prev} />} />
              <MetricCard title="Заказы на сайте" value={fmt(data.commerce.orders_all)} sub="оформлено на сайте, не оплата" color="var(--green)"
                footer={<Trend t={trends.orders_all} prev={prev} />} />
              <MetricCard title="Конверсия людей" value={data.commerce.people_cr == null ? '—' : `${pct(data.commerce.people_cr)}%`}
                sub={data.commerce.order_users == null ? 'посетители, оформившие заказ' : `${fmt(data.commerce.order_users)} из ${fmt(data.commerce.users)} посетителей`}
                color="var(--green)"
                footer={<Trend t={trends.people_cr} prev={prev} unit="%" digits={2} />} />
              <MetricCard title="Конверсия в заказ" value={`${pct(data.commerce.cr)}%`} sub={`${fmt(data.commerce.per1000)} заказов на 1000 визитов из поиска`}
                footer={<Trend t={trends.cr} prev={prev} unit="%" digits={2} />} />
              <MetricCard title="Оплачено заказов" value={data.commerce.paid ? fmt(data.commerce.paid.orders) : '—'} sub="сайт, по дате оплаты"
                footer={<Trend t={trends.paid_orders} prev={prev} />} />
              <MetricCard title="Оплачено, ₽" value={data.commerce.paid ? fmt(data.commerce.paid.sum) : '—'} sub="рубли с сайта, не из Метрики" color="#0e9f6e"
                footer={<Trend t={trends.paid_sum} prev={prev} unit=" ₽" />} />
              <MetricCard title="Показы в Яндексе" value={data.commerce.webmaster ? fmt(data.commerce.webmaster.shows) : '—'} sub="Вебмастер за период"
                footer={<Trend t={trends.shows} prev={prev} />} />
              <MetricCard title="Клики из Яндекса" value={data.commerce.webmaster ? fmt(data.commerce.webmaster.clicks) : '—'} sub={data.commerce.webmaster ? `CTR ${pct(data.commerce.webmaster.ctr)}%` : 'Вебмастер'} color="#00a7dc"
                footer={<Trend t={trends.clicks} prev={prev} />} />
            </div>
          )}

          {data.socials?.length > 0 && (
            <Card title="Соцсети: топ-5 за период" style={{ marginBottom: 16 }}>
              <SimpleTable
                headers={['Сеть', 'Визиты', 'Доля']}
                aligns={['left', 'right', 'right']}
                rows={data.socials.map(s => [
                  s.name, fmt(s.visits),
                  totals.social ? `${num(s.visits / totals.social * 100, 1)}%` : '—',
                ])}
              />
            </Card>
          )}

          {/* KD-X4 алерт (только keyacademy) */}
          {kdx4 && !kdx4.ok && (
            <Card style={{ marginBottom: 16, background: 'rgba(224,72,72,0.10)', borderColor: 'rgba(224,72,72,0.45)' }}>
              <div style={{ fontWeight: 800, color: '#e04848', marginBottom: 4 }}>⚠ Страница KD-X4 отдаёт {kdx4.status}</div>
              <div style={{ color: 'var(--text2)', fontSize: 13 }}>
                {kdx4.url}
                {kdx4.redirect
                  ? ` — сейчас редирект на ${kdx4.redirect}, это не живой товар.`
                  : ' — нужен 301-редирект на живой товар.'}
              </div>
            </Card>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 16, marginBottom: 16 }}>
            <Card title="Трафик за период: всего vs из поиска">
              <TrendChart monthly={data.monthly} />
              <div style={{ color: 'var(--text2)', fontSize: 11, marginTop: 6 }}>
                Источник: Яндекс.Метрика · ось X — дни/недели/месяцы (по длине периода).
              </div>
            </Card>
            <Card title="Поисковики (organic)">
              <EnginesPie engines={data.engines} />
            </Card>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <Card title="Топ поисковых фраз">
              <SimpleTable
                headers={['Запрос', 'Визиты', 'Тип']}
                aligns={['left', 'right', 'left']}
                rows={(data.phrases || []).slice(0, 14).map(p => [
                  p.name, p.visits,
                  <span style={{ color: p.type === 'бренд' ? '#00a7dc' : '#0e9f6e', fontSize: 12, fontWeight: 600 }}>{p.type}</span>,
                ])}
              />
            </Card>
            <Card title="Топ страниц входа из поиска">
              <SimpleTable
                headers={['Страница', 'Визиты']}
                aligns={['left', 'right']}
                rows={(data.pages || []).slice(0, 14).map(p => [p.name.replace(/^https?:\/\//, ''), p.visits])}
              />
            </Card>
          </div>

          {data.page_checks && (
            <Card title="Живая проверка статуса страниц" style={{ marginBottom: 16 }}>
              <SimpleTable
                headers={['Страница', 'HTTP', 'Статус']}
                aligns={['left', 'center', 'left']}
                rows={data.page_checks.map(pc => [
                  pc.label,
                  <span style={{ color: pc.ok ? '#0e9f6e' : '#e04848', fontWeight: 800 }}>{pc.status || 'ERR'}</span>,
                  pc.ok
                    ? <span style={{ color: 'var(--text2)' }}>{pc.status === 200 ? 'OK, индексируется' : '301 на живой товар'}</span>
                    : <span style={{ color: '#e04848', fontWeight: 600 }}>{pc.status === 404 ? 'Недоступна — нужен 301' : (pc.redirect ? `редирект → ${pc.redirect}` : 'недоступна')}</span>,
                ])}
              />
            </Card>
          )}

          {data.semcore && (
            <>
              <h2 style={{ fontSize: 18, fontWeight: 800, margin: '8px 0 16px' }}>Семантическое ядро</h2>
              <SemCore title={`${current?.name || ''} — приоритетные кластеры`} items={data.semcore} />
            </>
          )}

          <div style={{ color: 'var(--text2)', fontSize: 11, marginTop: 8 }}>
            Период: {data.period?.from} … {data.period?.to} · данные {data.cached ? 'из кэша' : 'обновлены'} · счётчик {data.counter}.
            {data.commerce ? ' Заказы — цель Метрики «Заказ оформлен». Рубли — оплаченные заказы сайта. Показы и клики — Яндекс.Вебмастер.' : ''}
          </div>
        </div>
      )}
    </div>
  );
}
